import React, { useEffect, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import axios from "../api/baseurl";
import { useNavigate } from "react-router";
import "./hm-theme.css";

const AddPemeDetails = ({ isOpen, onRequestClose, onSave, userDetails }) => {
  const [loading, setLoading] = useState(false);
  const [isDocModalOpen, setIsDocModalOpen] = useState(false);
  const [gender, setGender] = useState([]);
  const [pemeDetails, setPemeDetails] = useState({
    id: Date.now().toString(),
    fullName: "",
    phoneNumber: "",
    gender: "",
    dob: "",
    companyId: "",
    email: "",
    address: "",
    pemeDocuments: [],
  });

  const navigate = useNavigate();
  useEffect(() => {
    if (userDetails) {
      setPemeDetails({
        id: userDetails.id || Date.now().toString(),
        fullName: userDetails.fullName || "",
        phoneNumber: userDetails.phoneNumber || "",
        gender: userDetails.gender || "",
        dob: userDetails.dob || "",
        companyId: userDetails.companyId || "",
        email: userDetails.email || "",
        address: userDetails.address || "",
        pemeDocuments: userDetails.pemeDocuments || [],
      });
    }
  }, [userDetails]);

  function getBase64FromFile(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const [metadata, content] = reader.result.split(",");
        resolve({
          file: content, // Base64 content
          fileContentType: metadata, // MIME type
          fileSize: file.size,
          fileName: file.name,
        });
      };
      reader.onerror = (error) => reject(error);
    });
  }

  const [selectedFiles, setSelectedFiles] = useState([]);

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const base64Files = await Promise.all(files.map((file) => getBase64FromFile(file)));

    setSelectedFiles((prevFiles) => [...prevFiles, ...base64Files]);
  };

  const handleAddDocument = () => {
    if (!selectedFiles.length) return;

    setPemeDetails((prevState) => ({
      ...prevState,
      pemeDocuments: [...prevState.pemeDocuments, ...selectedFiles],
    }));

    setSelectedFiles([]);
    setIsDocModalOpen(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = JSON.parse(localStorage.getItem("data")).token;

        const gender = await axios.get(`/api/gender`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setGender(gender.data);
      } catch (error) {
        handleError(error);
      }
    };
    fetchData();
  }, []);

  function handleError(error) {
    if (error.response) {
      if (error.response.status === 401) {
        navigate(`/signin`);
        toast.error("Your session has expired. Please login.");
      } else if (error.response.data && error.response.data.message) {
        toast.error(error.response.data.message);
      } else if (error.response.data && error.response.data.code) {
        toast.error(error.response.data.code);
      } else {
        toast.error("An error occurred.");
      }
    } else if (error.message) {
      toast.error(error.message);
    } else {
      toast.error("An error occurred.");
    }
  }

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setPemeDetails((prevState) => ({
      ...prevState,
      [id]: value,
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (userDetails && userDetails.id) {
      onSave({ ...userDetails, ...pemeDetails });
    } else {
      onSave(pemeDetails);
    }

    onRequestClose();
    setLoading(false);
  };

  const handleRemoveDocument = (index) => {
    setPemeDetails((prevState) => ({
      ...prevState,
      pemeDocuments: prevState.pemeDocuments.filter((_, i) => i !== index),
    }));
  };

  const handleViewDocument = (doc) => {
    if (!doc || !doc.file) return;

    const byteCharacters = atob(doc.file);
    const byteNumbers = new Array(byteCharacters.length)
      .fill(0)
      .map((_, i) => byteCharacters.charCodeAt(i));
    const byteArray = new Uint8Array(byteNumbers);

    const fileType = doc.fileContentType.split(":")[1].split(";")[0];
    const blob = new Blob([byteArray], { type: fileType });
    const fileURL = URL.createObjectURL(blob);

    window.open(fileURL, "_blank");
  };

  return (
    <>
      <Modal show={isOpen} onHide={onRequestClose} centered size="lg">
        <Modal.Header closeButton className="hm-card-header">
          <Modal.Title className="hm-card-title">
            <i className="material-icons">person_add</i>
            {userDetails ? "Edit Candidate Details" : "Add Candidate Details"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          {loading && <div className="loading"></div>}
          <form onSubmit={handleSave}>
            <div className="row g-3 mb-3">
              <div className="col-md-6">
                <label className="form-label small fw-semibold">
                  Full Name <span className="text-danger">*</span>
                </label>
                <input
                  className="form-control"
                  id="fullName"
                  type="text"
                  value={pemeDetails.fullName}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="col-md-6">
                <label className="form-label small fw-semibold">
                  Email <span className="text-danger">*</span>
                </label>
                <input
                  className="form-control"
                  id="email"
                  type="email"
                  value={pemeDetails.email}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="col-md-4">
                <label className="form-label small fw-semibold">
                  Phone Number <span className="text-danger">*</span>
                </label>
                <input
                  className="form-control"
                  id="phoneNumber"
                  type="text"
                  value={pemeDetails.phoneNumber}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="col-md-4">
                <label className="form-label small fw-semibold">
                  Date of Birth <span className="text-danger">*</span>
                </label>
                <input
                  className="form-control"
                  id="dob"
                  type="date"
                  value={pemeDetails.dob}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="col-md-4">
                <label htmlFor="gender" className="form-label small fw-semibold">
                  Gender <span className="text-danger">*</span>
                </label>
                <select
                  className="form-select"
                  id="gender"
                  value={pemeDetails.gender}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select Gender</option>
                  {gender.map((cat) => (
                    <option key={cat.id} value={cat.gender}>
                      {cat.gender}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Documents Section */}
            <div className="d-flex align-items-center justify-content-between mb-3 pt-3 border-top">
              <div className="d-flex align-items-center gap-2">
                <h6 className="mb-0 fw-bold">Candidate Documents</h6>
                <span className="hm-badge hm-badge-primary">
                  {pemeDetails.pemeDocuments.length} Attached
                </span>
              </div>
              <button
                type="button"
                className="btn btn-sm btn-outline-success rounded-pill d-flex align-items-center gap-1"
                onClick={() => setIsDocModalOpen(true)}
              >
                Add Document
              </button>
            </div>

            <div className="hm-card mb-3" style={{ boxShadow: "none" }}>
              <div style={{ overflowX: "auto" }}>
                <table className="hm-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>File Name</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pemeDetails.pemeDocuments.length === 0 ? (
                      <tr>
                        <td colSpan="3">
                          <div className="hm-empty-state py-3">
                            <i className="material-icons">folder_open</i>
                            <p className="mb-0 small">No documents added for this candidate.</p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      pemeDetails.pemeDocuments.map((doc, index) => (
                        <tr key={index}>
                          <td>{index + 1}</td>
                          <td className="fw-semibold">{doc.fileName}</td>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              <button
                                type="button"
                                className="btn btn-outline-info btn-sm rounded-pill py-1 px-2.5 d-flex align-items-center gap-1"
                                onClick={() => handleViewDocument(doc)}
                              >
                                <FontAwesomeIcon icon={faEye} />
                                <span style={{ fontSize: "0.75rem" }}>View</span>
                              </button>
                              <button
                                type="button"
                                className="btn btn-outline-danger btn-sm rounded-pill py-1 px-2.5 d-flex align-items-center gap-1"
                                onClick={() => handleRemoveDocument(index)}
                              >
                                <FontAwesomeIcon icon={faTrash} />
                                <span style={{ fontSize: "0.75rem" }}>Remove</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="d-flex align-items-center justify-content-end gap-2 pt-3 border-top">
              <button
                type="button"
                className="btn btn-outline-secondary rounded-pill px-4"
                onClick={onRequestClose}
              >
                Cancel
              </button>
              <button type="submit" className="btn-hm-primary px-4" disabled={loading}>
                {loading ? "Saving..." : "Save Candidate"}
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>

      {/* Upload Documents Nested Modal */}
      <Modal show={isDocModalOpen} onHide={() => setIsDocModalOpen(false)} centered>
        <Modal.Header closeButton className="hm-card-header">
          <Modal.Title className="hm-card-title">
            <i className="material-icons">upload_file</i>
            Upload Documents
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <div className="mb-3">
            <label className="form-label small fw-semibold">Select Files</label>
            <input
              type="file"
              className="form-control"
              onChange={handleFileChange}
              multiple
            />
          </div>
          {selectedFiles.length > 0 && (
            <div className="mt-3">
              <label className="form-label small fw-semibold text-muted">Selected Files:</label>
              <div className="d-flex flex-wrap gap-2">
                {selectedFiles.map((file, index) => (
                  <span key={index} className="hm-badge hm-badge-info">
                    {file.fileName}
                  </span>
                ))}
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="border-top-0 pt-0">
          <button
            type="button"
            className="btn btn-outline-secondary rounded-pill px-3"
            onClick={() => setIsDocModalOpen(false)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn-hm-primary px-4"
            onClick={handleAddDocument}
            disabled={!selectedFiles.length}
          >
            Attach Selected Files
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AddPemeDetails;

